import React, { FC } from "react";
import { useQuery } from "@apollo/client";
import { useMutation } from "../hooks";
import { MatterTechnologiesEditor, TechnologyCharge } from "@design-system";
import { LIBRARY_TECHNOLOGIES_QUERY, TECHNOLOGY_UNITS_QUERY } from "../graphql/query";
import {
  DELETE_TECHNOLOGY_CHARGE,
  UPDATE_TECHNOLOGY_CHARGE,
  UPDATE_TECHNOLOGY_CHARGES,
  CREATE_AND_ADD_TECHNOLOGY_CHARGE,
} from "../graphql/mutation";
import { TECHNOLOGY_CHARGE_FRAGMENT } from "../graphql/fragment/technology-charge-fragment";
import { autoFocusTechnologyChargeLocalVar } from "~/graphql/client/local-variables";
import { Modal, Typography, Spin } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { createReorderingUpdates } from "./create-reordering-updates";
import { sortBy } from "lodash";

interface TechnologyTabContainerProps {
  matterId: number;
  technologyCharges: TechnologyCharge[];
  currency: { id: number; code: string; symbol: string };
}

export const TechnologyTabContainer: FC<TechnologyTabContainerProps> = ({
  matterId,
  technologyCharges,
  currency,
}) => {
  const { loading: libraryTechnologiesLoading, data: libraryTechnologiesData } = useQuery(
    LIBRARY_TECHNOLOGIES_QUERY
  );
  const { loading: technologyUnitsLoading, data: technologyUnitsData } =
    useQuery(TECHNOLOGY_UNITS_QUERY);

  const [createTechnologyCharge] = useMutation(CREATE_AND_ADD_TECHNOLOGY_CHARGE, {
    update(cache, { data }) {
      if (!data) return;
      const newTechnologyCharge = {
        ...data.createAndAddTechnologyCharge,
        id: data.createAndAddTechnologyCharge.id!,
        autoFocus: true,
      };
      const newTechnologyChargeRef = cache.writeFragment({
        data: newTechnologyCharge,
        fragment: TECHNOLOGY_CHARGE_FRAGMENT,
        fragmentName: "TechnologyChargeParts",
      });
      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          technologyCharges(existingTechnologyChargeRefs = [], { readField }) {
            if (
              existingTechnologyChargeRefs.some(
                (ref: any) => readField("id", ref) === data?.createAndAddTechnologyCharge
              )
            ) {
              return existingTechnologyChargeRefs;
            }
            return [...existingTechnologyChargeRefs, newTechnologyChargeRef];
          },
        },
      });
      autoFocusTechnologyChargeLocalVar(true);
    },
  });
  const [updateTechnologyCharge] = useMutation(UPDATE_TECHNOLOGY_CHARGE);
  const [updateTechnologyCharges] = useMutation(UPDATE_TECHNOLOGY_CHARGES);
  const [deleteTechnologyCharge] = useMutation(DELETE_TECHNOLOGY_CHARGE, {
    update(cache, { data }) {
      if (!data) return;
      cache.modify({
        id: `Matter:${matterId}`,
        fields: {
          technologyCharges(existingTechnologyChargeRefs = [], { readField }) {
            return existingTechnologyChargeRefs.filter(
              (technologyChargeRef: any) =>
                data.deleteTechnologyCharge.technologyChargeId !==
                readField("id", technologyChargeRef)
            );
          },
        },
      });
    },
  });

  const autoFocusOff = (): void => {
    autoFocusTechnologyChargeLocalVar(false);
  };

  const onReorder = async (activeId: number, overId: number) => {
    const { updates } = createReorderingUpdates(technologyCharges, activeId, overId);

    await updateTechnologyCharges({
      variables: {
        data: updates.map((x) => ({
          id: x.id,
          data: {
            order: { set: x.order },
          },
        })),
      },
      update: (cache) => {
        updates.forEach((update) => {
          const cacheId = cache.identify({
            __typename: "TechnologyCharge",
            id: update.id,
          });

          if (cacheId) {
            cache.modify({
              id: cacheId,
              fields: {
                order() {
                  return update.order;
                },
              },
            });
          }
        });
      },
    });
  };

  // Helper function to get rate values from a library technology based on currency
  const getRateValues = (libraryTechnology: any, currencyId: number) => {
    if (!libraryTechnology?.technologyRates?.length) {
      return {
        fixedAmount: 0,
        baseAmount: 0,
        percentage: 0,
        unitCost: 0,
      };
    }

    const matchingRate = libraryTechnology.technologyRates.find(
      (rate: any) => rate.currencyId === currencyId
    );

    if (!matchingRate) {
      return {
        fixedAmount: 0,
        baseAmount: 0,
        percentage: 0,
        unitCost: 0,
      };
    }

    return {
      fixedAmount: matchingRate.fixedAmount,
      baseAmount: matchingRate.baseAmount,
      percentage: matchingRate.percentage,
      unitCost: matchingRate.unitCost,
    };
  };

  return libraryTechnologiesLoading || technologyUnitsLoading ? (
    <Spin />
  ) : (
    <MatterTechnologiesEditor
      technologyCharges={sortBy(technologyCharges, "order")}
      libraryTechnologies={libraryTechnologiesData?.libraryTechnologies ?? []}
      technologyUnits={technologyUnitsData?.technologyUnits ?? []}
      onUpdate={(data) => {
        if (!data?.id) return;
        const originalTechnologyCharge = technologyCharges.find((tc) => tc.id === data.id);

        if (!originalTechnologyCharge) {
          return;
        }

        updateTechnologyCharge({
          variables: {
            technologyCharge: {
              id: data.id,
              data: {
                ...(data.libraryTechnologyId != undefined &&
                  data.description != undefined && {
                    libraryTechnology: { connect: { id: data.libraryTechnologyId } },
                    description: { set: data.description },
                  }),
                ...(data.fixedAmount != undefined && {
                  fixedAmount: { set: data.fixedAmount },
                }),
                ...(data.baseAmount != undefined && {
                  baseAmount: { set: data.baseAmount },
                }),
                ...(data.percentage != undefined && {
                  percentage: { set: data.percentage },
                }),
                ...(data.technologyUnitId != undefined && {
                  technologyUnit: { connect: { id: data.technologyUnitId } },
                }),
                ...(data.unitCost != undefined && {
                  unitCost: { set: data.unitCost },
                }),
                ...(data.units != undefined && {
                  units: { set: data.units },
                }),
                ...(data.type != undefined && {
                  type: { set: data.type },
                }),
                ...(data.notes != undefined && {
                  notes: { set: data.notes },
                }),
              },
            },
          },
          optimisticResponse: {
            updateTechnologyCharge: {
              __typename: "TechnologyCharge",
              ...originalTechnologyCharge,
              ...data,
              libraryTechnology: {
                ...originalTechnologyCharge.libraryTechnology,
                __typename: "LibraryTechnology",
                technologyUnit: {
                  ...originalTechnologyCharge.libraryTechnology.technologyUnit,
                  __typename: "TechnologyUnit",
                  createdBy: originalTechnologyCharge.libraryTechnology.technologyUnit.createdBy
                    ? {
                        __typename: "User",
                        ...originalTechnologyCharge.libraryTechnology.technologyUnit.createdBy,
                      }
                    : null,
                },
                technologyRates:
                  originalTechnologyCharge.libraryTechnology.technologyRates?.map((rate: any) => ({
                    __typename: "TechnologyRates",
                    ...rate,
                    currency: {
                      __typename: "Currency",
                      ...(rate.currency || {}),
                    },
                  })) || [],
                createdBy: originalTechnologyCharge.libraryTechnology.createdBy
                  ? {
                      __typename: "User",
                      ...originalTechnologyCharge.libraryTechnology.createdBy,
                    }
                  : null,
              },
              technologyUnit: {
                ...originalTechnologyCharge.technologyUnit,
                __typename: "TechnologyUnit",
                createdBy: originalTechnologyCharge.technologyUnit.createdBy
                  ? {
                      __typename: "User",
                      ...originalTechnologyCharge.technologyUnit.createdBy,
                    }
                  : null,
              },
            },
          },
        });
      }}
      onDelete={(technologyChargeId) =>
        Modal.confirm({
          width: 600,
          icon: <ExclamationCircleOutlined />,
          content: (
            <Typography.Text>{`Are you sure you want to delete this technology charge?`}</Typography.Text>
          ),
          onOk: async function () {
            deleteTechnologyCharge({
              variables: {
                technologyChargeId,
              },
              update: (cache) => {
                cache.evict({ id: `TechnologyCharge:${technologyChargeId}` });
              },
            });
          },
          onCancel() {},
        })
      }
      onCreate={(data) => {
        const libraryTechnology = data?.libraryTechnologyId
          ? libraryTechnologiesData?.libraryTechnologies.find(
              (lt) => lt.id === data?.libraryTechnologyId
            )
          : libraryTechnologiesData?.libraryTechnologies[0];
        const technologyUnit = data?.technologyUnitId
          ? technologyUnitsData?.technologyUnits.find((tu) => tu.id === data?.technologyUnitId)
          : technologyUnitsData?.technologyUnits[0];
        if (!libraryTechnology || !technologyUnit) return;

        // Get rate values based on the matter currency
        const rateValues = getRateValues(libraryTechnology, currency.id);

        const defaultValues = {
          libraryTechnologyId: libraryTechnology.id,
          technologyUnitId: technologyUnit.id,
          description: libraryTechnology.description,
          fixedAmount: rateValues.fixedAmount,
          baseAmount: rateValues.baseAmount,
          percentage: rateValues.percentage,
          type: libraryTechnology.type,
          unitCost: rateValues.unitCost,
          units: 0,
        };

        const values = { ...defaultValues, ...data };

        createTechnologyCharge({
          variables: {
            data: {
              matter: { connect: { id: matterId } },
              libraryTechnology: { connect: { id: values.libraryTechnologyId } },
              technologyUnit: { connect: { id: values.technologyUnitId } },
              description: values.description,
              fixedAmount: values.fixedAmount,
              baseAmount: values.baseAmount,
              percentage: values.percentage,
              order: technologyCharges.length || 0,
              type: values.type,
              unitCost: values.unitCost,
              units: values.units,
            },
          },
        });
      }}
      onReorder={onReorder}
      onAutoFocus={() => {
        autoFocusOff();
      }}
      currencySymbol={currency.symbol}
    />
  );
};
